// A bank account as a contribution source: selecting it means a new cash
// contribution funded from that account (DBO-1439). Composes the same
// summary/status pieces as the withdrawal destination rows, but owns its own
// validation key and expanded body (the contribution amount/fee fields).
import React from 'react';
import {Box} from '@mui/material';
import {PlanRow} from '../CreateBuilder/BuilderSourceDestination/PlanRow';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  BankAccountRowStatus,
  BankAccountRowSummary,
} from '../CreateBuilder/BuilderSourceDestination/BankAccountRowSummary';
import {BankAccountDetails} from '../CreateBuilder/BuilderSourceDestination/BankAccountDetails';
import {BuilderBankAccount} from '../../../schema-types/BuilderBankAccount';
import {BuilderContributeSourceFields} from './BuilderContributeSourceFields';

interface ContributeBankAccountSourceRowProps {
  bankAccount: BuilderBankAccount;
  isSelected: boolean;
  handleChange: (bankAccountId: string) => void;
}

export const ContributeBankAccountSourceRow: React.FC<
  ContributeBankAccountSourceRowProps
> = ({bankAccount, isSelected, handleChange}) => {
  const activeFormikKey = isSelected ? 'contribute.contributeFrom' : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);

  return (
    <PlanRow
      id={bankAccount.id}
      reccId={null}
      curoId={bankAccount.curoId ?? undefined}
      isSelected={isSelected}
      isSubplan={false}
    >
      <PlanAccordionShell
        expanded={isSelected}
        isSelected={isSelected}
        isSoleParent
        validationLevel={bgClass}
        summaryChildren={
          <BankAccountRowSummary
            bankAccount={bankAccount}
            isSelected={isSelected}
            handleChange={() => handleChange(bankAccount.id)}
          />
        }
        summaryTrailing={
          <>
            <BankAccountRowStatus bankAccount={bankAccount} ownershipLabel="Personal" />
            {activeFormikKey && <PlanValidationIcon formikKeyPrefix={activeFormikKey} />}
          </>
        }
      >
        <BankAccountDetails bankAccount={bankAccount} />
        <Box mt={1} />
        <BuilderContributeSourceFields />
      </PlanAccordionShell>
    </PlanRow>
  );
};
